import axios from "axios";

/** Normalize REACT_APP_BACKEND_URL and build the contact endpoint without duplicating /api. */
export function resolveContactApiUrl(backendUrl) {
  if (!backendUrl?.trim()) return null;

  const base = backendUrl.trim().replace(/\/+$/, "");
  if (base.endsWith("/api")) {
    return `${base}/Contact/CreateContactSubmission`;
  }

  return `${base}/api/Contact/CreateContactSubmission`;
}

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL?.trim() ??
  (process.env.NODE_ENV === "development" ? "" : null);

const CONTACT_API = resolveContactApiUrl(BACKEND_URL);

function getResponseValue(data, key) {
  if (!data || typeof data !== "object") return undefined;
  return data[key] ?? data[key.charAt(0).toUpperCase() + key.slice(1)];
}

export function isContactApiConfigured() {
  return CONTACT_API !== null;
}

export async function submitContactForm(formData) {
  if (!CONTACT_API) {
    throw new Error(
      "Contact form is not configured. Set REACT_APP_BACKEND_URL in your environment."
    );
  }

  const response = await axios.post(CONTACT_API, formData, {
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
    },
    validateStatus: (status) => status >= 200 && status < 300,
  });

  const success = getResponseValue(response.data, "success");
  const message = getResponseValue(response.data, "message");
  const submissionId = getResponseValue(response.data, "submissionId");

  if (!success || !submissionId) {
    throw new Error(message || "Submission failed. Please try again.");
  }

  return {
    message: message || "Thank you! We'll be in touch soon.",
    submissionId,
  };
}

export function parseContactFormErrors(error) {
  const apiErrors = error.response?.data?.errors;
  if (!apiErrors || typeof apiErrors !== "object") {
    return null;
  }

  const fieldMap = {
    Name: "name",
    Email: "email",
    Company: "company",
    Message: "message",
  };

  const serverErrors = {};
  Object.entries(apiErrors).forEach(([key, messages]) => {
    const field = fieldMap[key] || key.toLowerCase();
    serverErrors[field] = Array.isArray(messages) ? messages[0] : messages;
  });

  return Object.keys(serverErrors).length > 0 ? serverErrors : null;
}

export function getContactFormErrorMessage(error) {
  const responseData = error.response?.data;
  const apiMessage =
    getResponseValue(responseData, "message") ||
    getResponseValue(responseData, "error") ||
    getResponseValue(responseData, "title");

  if (apiMessage) return apiMessage;

  if (error.code === "ERR_NETWORK" || !error.response) {
    return "Unable to reach the server. Start the backend (cd backend && dotnet run) and confirm REACT_APP_BACKEND_URL=http://localhost:8001 in frontend/.env";
  }

  if (error.response?.status === 503) {
    return "Could not save to the database. Check http://localhost:8001/health/mongo — backend needs your Atlas connection string in appsettings.Local.json";
  }

  return error.message || "Something went wrong. Please try again.";
}
